import { Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, SimpleGrid, Button } from '@chakra-ui/react'
import { MsgExecuteContractCompat } from '@ninjablaze/sdk-ts'
import { useEffect, useState } from 'react'
import { useSession } from '@/context/SessionProvider'
import { LootboxContractExecuteMsgBuilder } from '@/api/lootbox/LootboxContract.msg-builder'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useAppToast } from '@/hooks/useAppToast'
import { useTranslation } from 'next-i18next/pages'
import { useLootboxContext } from '@/context/LootboxProvider'
import { useApolloClient } from '@apollo/client'
import { LootboxRewardDeprecated, MysteryReward } from '../../templates/LootboxRewardDeprecated'
import { LootboxLimitedDeprecated } from '../../templates/transformLootboxRewards'

export const LootboxRewardModal = ({
  onClose,
  callback,
  rewards,
}: {
  onClose: () => void
  callback?: () => void
  rewards: Array<{ rewardId: string; id: string; contract: string }>
}) => {
  return (
    <Modal closeOnEsc={false} isCentered={true} closeOnOverlayClick={false} size={'sm'} isOpen={true} onClose={onClose}>
      <ModalOverlay />
      <ModalContent marginLeft={2.5} marginRight={2.5}>
        <ModalHeader pb={2} fontSize="xl" fontWeight="700">
          Your reward
        </ModalHeader>
        <ModalBody pb={5}>
          <LootboxRewards rewards={rewards} callback={callback} onClose={onClose} />
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

const LootboxRewards = ({
  onClose,
  rewards,
  callback,
}: {
  onClose: () => void
  callback?: () => void
  rewards: Array<{ rewardId: string; id: string; contract: string }>
}) => {
  const { account, sendTransaction } = useSession()
  const queryClient = useQueryClient()
  const { getLootboxCollection } = useLootboxContext()
  const queryApolloClient = useApolloClient()
  const loots = rewards.map((reward) => ({
    contract: reward.contract,
    ...(getLootboxCollection(reward.contract) as LootboxLimitedDeprecated)?.rewards[reward.rewardId],
  }))

  const { onSuccess } = useAppToast()
  const { t } = useTranslation()
  const [sumbited, setSumbited] = useState(false)

  const { mutate, status, isPending } = useMutation({
    mutationFn: async () => {
      await sendTransaction(
        rewards.map((reward) =>
          MsgExecuteContractCompat.fromJSON({
            sender: account!.injectiveAddress,
            contractAddress: reward.contract,
            funds: [],
            msg: LootboxContractExecuteMsgBuilder.payout({
              payoutId: parseInt(reward.id),
              player: account?.injectiveAddress!,
            }),
          })
        ),
        {
          showSuccess: false,
          showError: true,
        }
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['USER_PROFILE_ME', account?.injectiveAddress],
      })

      queryClient.invalidateQueries({
        queryKey: ['USER_HYDRO_BALANCE', account?.injectiveAddress],
      })

      queryClient.invalidateQueries({
        queryKey: ['LOOTBOX_GAME_COUNT'],
      })
    },
  })

  useEffect(() => {
    mutate()

    return () => {
      queryApolloClient.refetchQueries({
        include: ['GetLootboxGames'],
      })
    }
  }, [])

  useEffect(() => {
    if (sumbited && status === 'success') {
      onSuccess({ custom: { title: t('messages.rewards-claimed') } })
      callback?.()
      onClose()
    }
  }, [sumbited, status])

  const handleOpen = async () => {
    setSumbited(true)

    if (status === 'pending' || status == 'success') {
      return
    }
    mutate()
  }

  return (
    <>
      <SimpleGrid columns={loots.length + 1 > 1 ? 2 : 1} gap={4} mb={4}>
        <MysteryReward short={true} contract="inj1quay03xd82t8t25amy0j0l2nycdm3wdemewcuy" />
        {loots.map((reward, index) => (
          <LootboxRewardDeprecated
            titleDenom={reward.contract === 'inj17n9euz3klayllr3a5tt5lt7fe7qpn3aehl5rvv' ? 'POINTS' : undefined}
            imageSize={180}
            key={index}
            {...reward}
            showChances={false}
            quantity={0}
            contract={reward.contract}
            glow={true}
          />
        ))}
      </SimpleGrid>
      <Button isLoading={sumbited && isPending} onClick={handleOpen} rounded={'xl'} w={'full'}>
        Claim rewards
      </Button>
    </>
  )
}
